import { IndicatorType } from '@melodic-ui/types';

/** 0 = Standard (grade assigned per lesson), 1 = Project (grade computed from project rubric criteria), 2 = Homework (grade derived from homework assignments; categories defined on the template). */
export enum GradingTemplateCategoryType {
	Standard = 0,
	Project = 1,
	Homework = 2
}

/** 1 = PerLesson (grade entered after each lesson), 2 = PerSemester (grade entered once at semester end — stored in SemesterCategoryGrade). */
export enum GradeFrequency {
	PerLesson = 1,
	PerSemester = 2
}

export interface IGradingTemplateFamily {
	gradingTemplateFamilyID: number;
	familyName: string;
	isActive: boolean;
	createDate: string;
	lastModifiedDate?: string;
}

export interface IGradingTemplateListItem {
	gradingTemplateID: number;
	gradingTemplateFamilyID: number;
	familyName: string;
	versionNumber: number;
	isActive: boolean;
	publishedDate?: string;
	gradeScaleID: number;
	gradeScaleName: string;
	/** False = enrollments on this template version are not graded (chirp rooms). */
	isGraded: boolean;
	createDate: string;
	lastModifiedDate?: string;
}

export interface IGradingTemplateDetail {
	gradingTemplateID: number;
	gradingTemplateFamilyID: number;
	familyName: string;
	versionNumber: number;
	isActive: boolean;
	publishedDate?: string;
	gradeScaleID: number;
	/** Attendance is taken per member (chirp room roster) for enrollments on this template version. */
	isMultiAttendance: boolean;
	/**
	 * False = enrollments on this template version are not graded (chirp rooms): grade UI, grade
	 * saves, grade notifications, and grade reports are all suppressed. Not-graded templates carry
	 * no grading categories.
	 */
	isGraded: boolean;
	/**
	 * Which completion checklist enrollments on this template version close out through. Null means
	 * the system default — the non-graded checklist — so a graded template sets the graded one here.
	 *
	 * Points at the workflow *family*, not a version: the enrollment resolves the family's published
	 * version when its tasks are created, so republishing a workflow never means editing templates.
	 */
	completionWorkflowFamilyID?: number | null;
	/** Display only; null whenever `completionWorkflowFamilyID` is. */
	completionWorkflowFamilyName?: string | null;
	createdByUserID: number;
	createDate: string;
	lastModifiedDate?: string;
	categories: IGradingTemplateCategoryDetail[];
	surveys: IGradingTemplateSurveyDetail[];
	homeworkCategories: IGradingTemplateHomeworkCategory[];
}

export interface IGradingTemplateSaveRequest {
	gradingTemplateFamilyID?: number;
	familyName: string;
	gradeScaleID: number;
	/** Per-member (chirp room) attendance for enrollments on this template version. */
	isMultiAttendance: boolean;
	/** False = not graded (chirp rooms); the request must then carry no categories. */
	isGraded: boolean;
	/** The completion checklist family, or null for the system default. */
	completionWorkflowFamilyID: number | null;
	categories: IGradingTemplateCategorySaveRequest[];
	surveys: IGradingTemplateSurveySaveRequest[];
	homeworkCategories: IGradingTemplateHomeworkCategorySaveRequest[];
}

export interface IGradingTemplateCategoryDetail {
	gradingTemplateCategoryID: number;
	gradingTemplateID: number;
	categoryName: string;
	weightPercent: number;
	categoryType: GradingTemplateCategoryType;
	gradeFrequency: GradeFrequency;
	gradeReportColumnID?: number | null;
	orderNumber: number;
	rubrics: IGradingTemplateRubric[];
	criteria: IGradingTemplateCriteriaDetail[];
	milestones: IGradingTemplateMilestone[];
}

export interface IGradingTemplateCategorySaveRequest {
	categoryName: string;
	weightPercent: number;
	categoryType: GradingTemplateCategoryType;
	gradeFrequency: GradeFrequency;
	gradeReportColumnID?: number | null;
	orderNumber: number;
	rubrics: IGradingTemplateRubricSaveRequest[];
	criteria: IGradingTemplateCriteriaSaveRequest[];
	milestones: IGradingTemplateMilestoneSaveRequest[];
}

export interface IGradingTemplateRubric {
	rubricID: number;
	gradingTemplateCategoryID: number;
	gradeID: number;
	gradeName: string;
	description: string;
	gradeColor: IndicatorType;
}

export interface IGradingTemplateRubricSaveRequest {
	gradeID: number;
	description: string;
}

export interface IGradingTemplateCriteriaDetail {
	criteriaID: number;
	gradingTemplateCategoryID: number;
	criteriaName: string;
	weightPercent: number;
	gradeReportColumnID?: number | null;
	orderNumber: number;
	rubrics: IGradingTemplateCriteriaRubric[];
}

export interface IGradingTemplateCriteriaSaveRequest {
	criteriaName: string;
	weightPercent: number;
	gradeReportColumnID?: number | null;
	orderNumber: number;
	rubrics: IGradingTemplateCriteriaRubricSaveRequest[];
}

export interface IGradingTemplateCriteriaRubric {
	rubricID: number;
	criteriaID: number;
	gradeID: number;
	gradeName: string;
	gradeColor: IndicatorType;
	description: string;
}

export interface IGradingTemplateCriteriaRubricSaveRequest {
	gradeID: number;
	description: string;
}

export interface IGradingTemplateMilestone {
	milestoneID: number;
	gradingTemplateCategoryID: number;
	milestoneName: string;
	description?: string;
	gradeReportColumnID?: number | null;
	orderNumber: number;
}

export interface IGradingTemplateMilestoneSaveRequest {
	milestoneName: string;
	description?: string;
	gradeReportColumnID?: number | null;
	orderNumber: number;
}

export interface IGradingTemplateSurveySaveRequest {
	surveyName: string;
	description?: string;
	surveyType: number;
	respondentType: number;
	semesterLessonTypeID: number;
	orderNumber: number;
	/** Days before the target lesson the survey opens. Always sent — saving a template re-inserts its
	 * surveys, so omitting it would reset a configured lead time to the column default. */
	availableDaysBefore: number;
	questions?: IGradingTemplateSurveyQuestionSaveRequest[];
}

/** SurveyType: 1=SelfReflection, 2=InstructorEval. RespondentType: 1=Learner, 2=Instructor. */
export interface IGradingTemplateSurveyDetail {
	surveyID: number;
	gradingTemplateID: number;
	surveyName: string;
	description?: string;
	surveyType: number;
	respondentType: number;
	semesterLessonTypeID: number;
	orderNumber: number;
	/**
	 * Days before its target lesson that this survey may be answered — semesterLessonTypeID says which
	 * lesson, this says when it opens on that lesson. Optional because a frontend deploy can lead the
	 * API; read it through isSurveyAvailable(), which falls back to the same default as the column.
	 */
	availableDaysBefore?: number;
	/**
	 * True when any question is a CourseObjectives question (questionType 5). Survey lists hide the
	 * survey when the enrollment's planned curriculum has no objectives to expand it into.
	 */
	hasCourseObjectivesQuestion?: boolean;
	/**
	 * Where this survey's responses are stored, from its trigger: 1 = Enrollment, 2 = Semester,
	 * 3 = Lesson (SurveyResponseScope in survey-response.util). Read-only, and optional because a
	 * frontend deploy can lead the API.
	 */
	responseScope?: number;
	questions?: IGradingTemplateSurveyQuestion[];
}

/** QuestionType: 1=Text, 2=Numeric, 3=EmojiScale, 4=MultipleChoice, 5=CourseObjectives (one emoji rating per planned-curriculum objective). Numeric: minValue/maxValue optional bounds. EmojiScale: emojiScaleSize 3|4|5 (default 5). */
export interface IGradingTemplateSurveyQuestion {
	surveyQuestionID: number;
	surveyID: number;
	questionText: string;
	questionType: number;
	orderNumber: number;
	isOptional: boolean;
	options?: IGradingTemplateSurveyQuestionOption[];
	minValue?: number;
	maxValue?: number;
	emojiScaleSize?: number;
}

export interface IGradingTemplateSurveyQuestionOption {
	optionID: number;
	surveyQuestionID: number;
	optionText: string;
	orderNumber: number;
}

export interface IGradingTemplateSurveyQuestionSaveRequest {
	questionText: string;
	questionType: number;
	orderNumber: number;
	isOptional: boolean;
	options?: string[];
	minValue?: number;
	maxValue?: number;
	emojiScaleSize?: number;
}

export interface IGradingTemplateHomeworkCategory {
	homeworkCategoryID: number;
	gradingTemplateID: number;
	categoryName: string;
	description?: string;
	orderNumber: number;
}

export interface IGradingTemplateHomeworkCategorySaveRequest {
	categoryName: string;
	description?: string;
	orderNumber: number;
}
